import { ArrowRightLeft, Bot, HelpCircle, Sparkles, TrendingDown, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { currency, formatError, toApiInput, type UiValues } from './_shared';
import { subscribeOpenRecovery, subscribeOpenSuggest } from './calc-assist-events';
import { type CalcAssistPatch, type CalcAssistQuestion, useCalcAssist } from './use-calc-assist';
import {
  type RecoveryOption,
  type RecoveryPatch,
  useRecoveryOptions,
} from './use-recovery-options';
import { type GarmentSuggestion, useSuggestGarment } from './use-suggest-garment';

/**
 * "Ask AI" drawer. Opens from a trigger button in the result panel's
 * action bar and offers three canned questions grounded in the current
 * calc — one deterministic (how_low) and two LLM-backed (explain_floor
 * / why_margin). Each question calls POST /calc/assist and renders
 * the prose answer + any one-click action (e.g., "Apply 12% discount").
 */
export function AskAiDrawer({
  form,
  values,
  className,
}: {
  form: UseFormReturn<UiValues>;
  values: UiValues;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const { state, ask, reset } = useCalcAssist();
  const suggestHook = useSuggestGarment();
  const recoveryHook = useRecoveryOptions();
  const [activeMode, setActiveMode] = useState<'assist' | 'suggest' | 'recovery' | null>(null);
  // Owner-tunable margin target for how_low. Persists per-session so
  // repeat clicks honour the previous selection.
  const [howLowTarget, setHowLowTarget] = useState<number>(40);

  function handleAsk(question: CalcAssistQuestion) {
    setActiveMode('assist');
    const input = toApiInput(values) as Parameters<typeof ask>[1];
    const target = question === 'how_low' ? howLowTarget : undefined;
    void ask(question, input, target);
  }

  function handleSuggestGarment() {
    setActiveMode('suggest');
    const input = toApiInput(values) as Parameters<typeof suggestHook.suggest>[0];
    // Pass the user's preferred color when they've set one — tightens
    // the search via cascadeColorMatch on the backend.
    const color = values.colorName?.trim() || undefined;
    void suggestHook.suggest(input, color);
  }

  function handleRecoveryOptions(category?: 'tee' | 'hoodie') {
    setActiveMode('recovery');
    const input = toApiInput(values) as Parameters<typeof recoveryHook.run>[0];
    const color = values.colorName?.trim() || undefined;
    void recoveryHook.run(input, color, category);
  }

  // Listen for the parse textbox's "user wants a suggestion" CTA. Opens
  // the drawer and runs the suggest query against whatever the form
  // holds at click time (reads via form.getValues so we don't get a
  // stale closure on `values`). The event detail carries the parse's
  // garment category hint ("tee" / "hoodie") so we only rank
  // candidates of the matching shape.
  useEffect(() => {
    return subscribeOpenSuggest((detail) => {
      const fresh = form.getValues();
      setOpen(true);
      setActiveMode('suggest');
      const input = toApiInput(fresh) as Parameters<typeof suggestHook.suggest>[0];
      const color = fresh.colorName?.trim() || undefined;
      void suggestHook.suggest(input, color, detail.category);
    });
  }, [form, suggestHook]);

  // Passive recovery banner (under the margin gauge) fires this when
  // the live margin drops below target. Opens the drawer in recovery
  // mode and runs the lever search against the fresh form state.
  useEffect(() => {
    return subscribeOpenRecovery((detail) => {
      const fresh = form.getValues();
      setOpen(true);
      setActiveMode('recovery');
      const input = toApiInput(fresh) as Parameters<typeof recoveryHook.run>[0];
      const color = fresh.colorName?.trim() || undefined;
      void recoveryHook.run(input, color, detail.category);
    });
  }, [form, recoveryHook]);

  function handleApply(patch: CalcAssistPatch) {
    for (const [key, value] of Object.entries(patch) as [keyof CalcAssistPatch, unknown][]) {
      if (value === undefined) continue;
      form.setValue(key as keyof UiValues, value as never, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }

  function handleApplySuggestion(suggestion: GarmentSuggestion) {
    // Switch the garment to SS-priced and fill the style/color so the
    // calculator re-prices the exact same scenario against this blank.
    form.setValue('garmentSource', 'we_provide_by_style', { shouldDirty: true });
    form.setValue('style', suggestion.style, { shouldDirty: true });
    form.setValue('colorName', suggestion.colorName, { shouldDirty: true });
  }

  function handleApplyRecovery(patch: RecoveryPatch) {
    for (const [key, value] of Object.entries(patch) as [keyof RecoveryPatch, unknown][]) {
      if (value === undefined) continue;
      form.setValue(key as keyof UiValues, value as never, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }

  function handleClose(next: boolean) {
    setOpen(next);
    if (!next) {
      reset();
      suggestHook.reset();
      recoveryHook.reset();
      setActiveMode(null);
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className={`gap-1.5 ${className ?? ''}`}>
          <Bot className="size-3.5" />
          <span>Ask AI</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="space-y-1 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Bot className="size-4" />
            Ask AI
          </SheetTitle>
          <SheetDescription>
            Quick questions about the current quote. Numbers always come from the pricing engine —
            the AI only explains.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3">
          <div className="rounded-lg border bg-background p-3">
            <QuestionCard
              icon={<TrendingDown className="size-4" />}
              title="How low can I go?"
              subtitle={`Max discount that still clears ${howLowTarget}% margin.`}
              onClick={() => handleAsk('how_low')}
              active={state.status !== 'idle' && state.question === 'how_low'}
              flat
            />
            <div className="mt-2 flex items-center gap-1.5 pl-11">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Target
              </span>
              {[30, 40, 50, 60].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHowLowTarget(n);
                  }}
                  className={`rounded border px-2 py-0.5 text-[11px] tabular-nums transition-colors ${
                    howLowTarget === n
                      ? 'border-primary bg-primary/10 font-medium text-primary'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted/40'
                  }`}
                >
                  {n}%
                </button>
              ))}
            </div>
          </div>
          <QuestionCard
            icon={<HelpCircle className="size-4" />}
            title="Explain my floor"
            subtitle="Why the engine says you can't go below a certain price."
            onClick={() => handleAsk('explain_floor')}
            active={state.status !== 'idle' && state.question === 'explain_floor'}
          />
          <QuestionCard
            icon={<Sparkles className="size-4" />}
            title="Why is margin tight?"
            subtitle="Which cost drivers are eating the margin."
            onClick={() => handleAsk('why_margin')}
            active={state.status !== 'idle' && state.question === 'why_margin'}
          />
          <QuestionCard
            icon={<ArrowRightLeft className="size-4" />}
            title="Suggest a cheaper garment"
            subtitle="Top 3 SS blanks priced for this exact job."
            onClick={handleSuggestGarment}
            active={activeMode === 'suggest'}
          />
          <QuestionCard
            icon={<Wrench className="size-4" />}
            title="Show my options"
            subtitle="Parallel recovery levers — qty, print, discount, garment."
            onClick={() => handleRecoveryOptions()}
            active={activeMode === 'recovery'}
          />
        </div>

        {activeMode === 'suggest' ? (
          <SuggestionsPane state={suggestHook.state} onApply={handleApplySuggestion} />
        ) : activeMode === 'recovery' ? (
          <RecoveryPane state={recoveryHook.state} onApply={handleApplyRecovery} />
        ) : activeMode === 'assist' ? (
          <AnswerPane state={state} onApply={handleApply} />
        ) : (
          <p className="mt-6 rounded-md border border-dashed bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            Pick a question above to get started.
          </p>
        )}
      </SheetContent>
    </Sheet>
  );
}

function QuestionCard({
  icon,
  title,
  subtitle,
  onClick,
  active,
  flat,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  active: boolean;
  /** When true, drop the card border so it can be embedded in another container. */
  flat?: boolean;
}) {
  const wrap = flat
    ? `flex w-full items-start gap-3 rounded-md p-1 text-left transition-colors hover:bg-muted/40 ${active ? 'bg-primary/5' : ''}`
    : `flex w-full items-start gap-3 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/40 ${active ? 'border-primary/50 ring-1 ring-primary/30' : ''}`;
  return (
    <button type="button" onClick={onClick} className={wrap}>
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted/40 text-muted-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{subtitle}</span>
      </span>
    </button>
  );
}

function AnswerPane({
  state,
  onApply,
}: {
  state: ReturnType<typeof useCalcAssist>['state'];
  onApply: (patch: CalcAssistPatch) => void;
}) {
  if (state.status === 'idle') return null;
  if (state.status === 'loading') {
    return (
      <div className="mt-6 space-y-2 rounded-md border bg-card p-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-8/12" />
      </div>
    );
  }
  if (state.status === 'error') {
    const { title, message } = formatError(state.error);
    return (
      <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-xs">{message}</p>
      </div>
    );
  }

  const { answer, actions, metrics } = state.result;
  return (
    <div className="mt-6 space-y-3 rounded-md border bg-card p-4">
      <p className="text-sm leading-relaxed">{answer}</p>
      {metrics && <MetricsRow metrics={metrics} />}
      {actions && actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t pt-3">
          {actions.map((action) => (
            <Button
              key={action.label}
              type="button"
              size="sm"
              onClick={() => onApply(action.patch)}
              className="h-8 text-xs"
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricsRow({
  metrics,
}: {
  metrics: {
    maxDiscountPct?: number;
    resultingMarginPct?: number;
    resultingPerUnitSell?: number;
    resultingOrderTotal?: number;
  };
}) {
  return (
    <dl className="grid grid-cols-2 gap-2 rounded-md bg-muted/30 p-2 text-xs">
      {metrics.maxDiscountPct !== undefined && (
        <Metric label="Max discount" value={`${metrics.maxDiscountPct}%`} />
      )}
      {metrics.resultingMarginPct !== undefined && (
        <Metric label="Resulting margin" value={`${metrics.resultingMarginPct.toFixed(1)}%`} />
      )}
      {metrics.resultingPerUnitSell !== undefined && (
        <Metric label="Per unit" value={currency(metrics.resultingPerUnitSell)} />
      )}
      {metrics.resultingOrderTotal !== undefined && (
        <Metric label="Order total" value={currency(metrics.resultingOrderTotal)} />
      )}
    </dl>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function SuggestionsPane({
  state,
  onApply,
}: {
  state: ReturnType<typeof useSuggestGarment>['state'];
  onApply: (s: GarmentSuggestion) => void;
}) {
  if (state.status === 'idle') return null;
  if (state.status === 'loading') {
    return (
      <div className="mt-6 space-y-2 rounded-md border bg-card p-4">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (state.status === 'error') {
    const { title, message } = formatError(state.error);
    return (
      <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-xs">{message}</p>
      </div>
    );
  }

  const { suggestions, summary, baseline } = state.result;
  return (
    <div className="mt-6 space-y-3 rounded-md border bg-card p-4">
      <p className="text-sm leading-relaxed">{summary}</p>
      {baseline && (
        <p className="text-[11px] text-muted-foreground">
          Current: {currency(baseline.perUnitSell)}/ea · {baseline.marginPct.toFixed(1)}% margin.
        </p>
      )}
      {suggestions.length === 0 ? null : (
        <ul className="space-y-2">
          {suggestions.map((s) => (
            <SuggestionRow key={`${s.style}-${s.colorName}`} suggestion={s} onApply={onApply} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RecoveryPane({
  state,
  onApply,
}: {
  state: ReturnType<typeof useRecoveryOptions>['state'];
  onApply: (patch: RecoveryPatch) => void;
}) {
  if (state.status === 'idle') return null;
  if (state.status === 'loading') {
    return (
      <div className="mt-6 space-y-2 rounded-md border bg-card p-4">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }
  if (state.status === 'error') {
    const { title, message } = formatError(state.error);
    return (
      <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-xs">{message}</p>
      </div>
    );
  }

  const { options, summary, baseline } = state.result;
  return (
    <div className="mt-6 space-y-3 rounded-md border bg-card p-4">
      <p className="text-sm leading-relaxed">{summary}</p>
      {baseline && (
        <p className="text-[11px] text-muted-foreground">
          Current: {currency(baseline.perUnitSell)}/ea · {baseline.marginPct.toFixed(1)}% margin.
        </p>
      )}
      {options.length === 0 ? null : (
        <ul className="space-y-2">
          {options.map((o) => (
            <RecoveryRow key={`${o.kind}-${o.label}`} option={o} onApply={onApply} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RecoveryRow({
  option,
  onApply,
}: {
  option: RecoveryOption;
  onApply: (patch: RecoveryPatch) => void;
}) {
  return (
    <li className="space-y-1.5 rounded-md border bg-background p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{option.label}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{option.detail}</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 shrink-0 text-xs"
          onClick={() => onApply(option.patch)}
        >
          Apply
        </Button>
      </div>
      <dl className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
        <div className="flex items-baseline gap-1">
          <dt className="text-muted-foreground">Sell</dt>
          <dd className="font-medium">{currency(option.perUnitSell)}/ea</dd>
        </div>
        <div className="flex items-baseline gap-1">
          <dt className="text-muted-foreground">Total</dt>
          <dd className="font-medium">{currency(option.orderTotal)}</dd>
        </div>
        <div className="flex items-baseline gap-1">
          <dt className="text-muted-foreground">Margin</dt>
          <dd className="font-medium">{option.marginPct.toFixed(1)}%</dd>
        </div>
        <div className="flex items-baseline gap-1 text-emerald-600">
          <dt>Δ</dt>
          <dd className="font-medium">+{option.marginDelta.toFixed(1)} pts</dd>
        </div>
      </dl>
    </li>
  );
}

function SuggestionRow({
  suggestion,
  onApply,
}: {
  suggestion: GarmentSuggestion;
  onApply: (s: GarmentSuggestion) => void;
}) {
  const s = suggestion;
  return (
    <li className="space-y-1.5 rounded-md border bg-background p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {s.style} <span className="text-muted-foreground">· {s.colorName}</span>
          </p>
          {s.subtitle && <p className="text-[11px] text-muted-foreground">{s.subtitle}</p>}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 shrink-0 text-xs"
          onClick={() => onApply(s)}
        >
          Use this
        </Button>
      </div>
      <dl className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
        <div className="flex items-baseline gap-1">
          <dt className="text-muted-foreground">Sell</dt>
          <dd className="font-medium">{currency(s.perUnitSell)}/ea</dd>
        </div>
        <div className="flex items-baseline gap-1">
          <dt className="text-muted-foreground">Total</dt>
          <dd className="font-medium">{currency(s.orderTotal)}</dd>
        </div>
        <div className="flex items-baseline gap-1">
          <dt className="text-muted-foreground">Margin</dt>
          <dd className="font-medium">{s.marginPct.toFixed(1)}%</dd>
        </div>
        {s.savingsPerUnit > 0 && (
          <div className="flex items-baseline gap-1 text-emerald-600">
            <dt>Saves</dt>
            <dd className="font-medium">
              {currency(s.savingsPerUnit)}/ea · {currency(s.savingsOrderTotal)} total
            </dd>
          </div>
        )}
      </dl>
    </li>
  );
}
